import React, { useState } from 'react';
import { Send, Sparkles } from 'lucide-react';

/**
 * ChatInput v3 — premium concierge composer with focus "listening…" hint.
 *
 * V3.5 polish notes:
 *   • On focus, the leading Sparkles icon starts a soft breathing pulse
 *     and a "listening…" microcopy appears to the right — signals the
 *     concierge is attentive without ever making a false claim.
 *   • Placeholder unchanged (still parent-supplied).
 *   • On blur or when the user has typed, the "listening…" hint dismisses.
 *   • Send button keeps the canonical solid-blue treatment.
 *
 * Pure presentation — parent still owns value / onChange / submit / disabled.
 */
export default function ChatInput({ value, onChange, onSubmit, disabled, placeholder, listeningLabel = 'listening…' }) {
  const [focused, setFocused] = useState(false);
  const showListening = focused && !value?.length && !disabled;
  return (
    <form
      onSubmit={onSubmit}
      className="px-3 sm:px-4 pt-3 pb-4 border-t border-gray-100 bg-white"
      data-testid="chat-input-form"
    >
      <div className="flex items-end gap-2">
        <div className="flex-1 relative">
          <Sparkles
            aria-hidden
            className={`pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 transition-transform ${
              focused ? 'motion-safe:animate-pulse' : ''
            }`}
          />
          {/* Focus microcopy — quietly signals attention without a lie. */}
          <span
            aria-hidden
            data-testid="chat-input-listening"
            className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest font-bold text-emerald-600 flex items-center gap-1.5 transition-opacity duration-200 ${
              showListening ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <span className="relative flex items-center justify-center">
              <span className="absolute w-2 h-2 rounded-full bg-emerald-500/50 animate-ping" />
              <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500" />
            </span>
            {listeningLabel}
          </span>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            className={`w-full pl-10 pr-4 py-3 bg-gray-50 border rounded-xl text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all ${
              focused ? 'border-blue-300' : 'border-gray-200'
            }`}
            disabled={disabled}
            data-testid="chat-input"
            enterKeyHint="send"
            autoComplete="off"
          />
        </div>
        <button
          type="submit"
          disabled={disabled || !value.trim()}
          data-testid="chat-send-btn"
          aria-label="Send message"
          className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-600/20 hover:shadow-lg hover:shadow-blue-600/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}
